import { SparkleIcon } from "@/components/icons";
import { SectionHeader } from "@/components/SectionHeader";
import { SITE } from "@/lib/site";
import chartFill from "@/assets/chart-fill.svg";
import chartLine from "@/assets/chart-line.svg";
import connectDevices from "@/assets/connect-devices.svg";
import respondFloors from "@/assets/respond-floors.svg";
import { ButtonLink } from "@/components/ui/Button";

export function IntelligenceSection() {
  return (
    <section
      id="intelligence"
      className="border-y border-primary px-8 py-20 md:border-0 md:px-6 md:py-16"
    >
      <div className="mx-auto flex max-w-2xl flex-col items-center gap-16 text-center">
        <SectionHeader
          variant="centered"
          icon={<SparkleIcon className="size-10 text-brand" />}
          title="The Intelligence Behind Every Room"
          body="The best hospitality technology should work for everyone it touches. Somo is designed to improve what guests experience, how properties perform, and how efficiently they use the resources behind every stay."
        />
        <ButtonLink href={`${SITE}/meet-somo`} size="lg">
          Learn More
        </ButtonLink>
      </div>

      <div className="mx-auto mt-16 grid max-w-6xl gap-4 lg:grid-cols-5">
        <Card className="min-h-110 md:min-h-80 lg:col-span-2 lg:row-span-2">
          <img
            loading="lazy"
            decoding="async"
            src={connectDevices}
            alt=""
            className="absolute left-10 top-10 w-4/5 md:left-auto md:right-0 md:top-6 md:w-1/2 lg:inset-x-0 lg:top-[6%] lg:w-full"
          />
          <CardCopy
            title="Connect"
            body="Bring the systems, devices, and data across a space together into one connected environment."
          />
        </Card>

        <Card className="min-h-110 md:min-h-80 lg:col-span-3">
          <div className="absolute left-8 right-0 top-8 flex flex-col gap-3 md:left-auto md:right-6 md:top-12 md:w-80">
            <div className="flex flex-col gap-1 rounded-xl bg-tertiary px-6 py-3">
              <p className="text-base font-medium leading-6 text-primary">
                Room 8 - Floor 1
              </p>
              <div className="flex gap-1">
                <StatusPill>Occupied</StatusPill>
                <StatusPill>Critical</StatusPill>
              </div>
            </div>
            <div className="relative h-32 overflow-hidden rounded-xl border border-primary p-5">
              <p className="text-sm leading-5 text-primary/60">
                Room’s energy usage
              </p>
              <p className="text-base font-medium leading-6 text-primary">
                20kW
              </p>
              <img
                loading="lazy"
                decoding="async"
                src={chartFill}
                alt=""
                className="absolute left-0 top-20 w-full"
              />
              <img
                loading="lazy"
                decoding="async"
                src={chartLine}
                alt=""
                className="absolute bottom-3.5 left-0 w-full"
              />
            </div>
          </div>
          <CardCopy
            title="Understand"
            body="Turn signals like occupancy, temperature, and guest activity into an understanding of what’s happening in each room."
          />
        </Card>

        <Card className="min-h-110 md:min-h-80 lg:col-span-3">
          <div className="absolute -right-20 -top-20 w-96 md:-right-8 md:-top-24">
            <img
              loading="lazy"
              decoding="async"
              src={respondFloors}
              alt=""
              className="w-full"
            />
            <span
              aria-hidden
              className="absolute left-[40%] top-1/2 hidden h-8 w-px bg-accent lg:block"
            />
          </div>
          <CardCopy
            title="Respond"
            body="Use that intelligence to automatically adjust room conditions, automate operations, and reduce unnecessary energy use."
          />
        </Card>
      </div>
    </section>
  );
}

function Card({
  className,
  children,
}: {
  className: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`relative overflow-hidden rounded-card border border-primary ${className}`}
    >
      {children}
    </div>
  );
}

function CardCopy({ title, body }: { title: string; body: string }) {
  return (
    <div className="absolute inset-x-8 bottom-8 flex flex-col gap-4 md:bottom-10 md:left-10 md:right-auto md:w-60">
      <h3 className="text-balance text-lg leading-title text-primary md:text-2xl md:leading-normal">
        {title}
      </h3>
      <p className="text-sm leading-normal md:text-base tracking-body text-secondary">
        {body}
      </p>
    </div>
  );
}

function StatusPill({ children }: { children: string }) {
  return (
    <span className="rounded-full bg-tertiary px-3 font-mono text-xs font-medium uppercase leading-4 text-tertiary md:leading-5">
      {children}
    </span>
  );
}
